import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CommonResponse } from '../../models';
import { environment } from '../../../../environments/environment';
import { ProfileUser, ProfileUserUniversitySchoolUniversity } from '../../models/profile/profile';
import { LogBook } from '../../models/registration/log-book';
import { map } from 'rxjs/operators';
import { File } from '../../models/file';
import { Evaluation } from '../../models/evaluation/evaluation';
import { CareerYear } from '../../models/career/career';

@Injectable({
	providedIn: 'root',
})
export class DirectorService {
	constructor(private _http: HttpClient) {}

	private buildHeaders(school_id?: string, career_id?: string): { headers?: HttpHeaders } {
		if (school_id && career_id) {
			return {
				headers: new HttpHeaders({
					'X-SchoolUniversity': school_id,
					'X-Career': career_id,
				}),
			};
		}
		return {};
	}

	getTraineeFilteredByDirector(): Observable<CommonResponse<Array<ProfileUserUniversitySchoolUniversity>>> {
		return this._http.get<CommonResponse<Array<ProfileUserUniversitySchoolUniversity>>>(
			`${environment.apiSchoolUrl}/profiles_users_universities_schools_universities/trainees/filtered_by/director`
		);
	}

	getTutorsFilteredByDirector(): Observable<CommonResponse<Array<ProfileUserUniversitySchoolUniversity>>> {
		return this._http.get<CommonResponse<Array<ProfileUserUniversitySchoolUniversity>>>(
			`${environment.apiSchoolUrl}/profiles_users_universities_schools_universities/tutors/filtered_by/director`
		);
	}

	getOperationalTutorsFilteredByDirector(): Observable<CommonResponse<Array<ProfileUser>>> {
		return this._http.get<CommonResponse<Array<ProfileUser>>>(
			`${environment.apiSchoolUrl}/profiles_users/operational_tutors/filtered_by/director`
		);
	}

	getLastTenRegistration(): Observable<CommonResponse<Array<LogBook>>> {
		return this._http.get<CommonResponse<Array<LogBook>>>(`${environment.apiSchoolUrl}/career/director/log_books/last_ten`).pipe(
			map(response => {
				if (response.data) {
					response.data.forEach(logBook => {
						const totQuantity = logBook.log_book_details?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
						const totMinutes = logBook.log_book_details?.reduce((acc, item) => acc + item.minutes, 0) ?? 0;
						logBook.tot_quantity = totQuantity;
						logBook.tot_minutes = totMinutes;
					});
				}
				return response;
			})
		);
	}

	getResgistrationsList(): Observable<CommonResponse<Array<LogBook>>> {
		return this._http.get<CommonResponse<Array<LogBook>>>(`${environment.apiSchoolUrl}/career/director/log_books`).pipe(
			map(response => {
				if (response.data) {
					response.data.forEach(logBook => {
						const totQuantity = logBook.log_book_details?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
						const totMinutes = logBook.log_book_details?.reduce((acc, item) => acc + item.minutes, 0) ?? 0;
						logBook.tot_quantity = totQuantity;
						logBook.tot_minutes = totMinutes;
					});
				}
				return response;
			})
		);
	}

	getRegistrationFilteredByYear(year: string): Observable<CommonResponse<Array<LogBook>>> {
		return this._http
			.get<CommonResponse<Array<LogBook>>>(`${environment.apiSchoolUrl}/career/director/log_books/filtered_by/year/${year}`)
			.pipe(
				map(response => {
					if (response.data) {
						response.data.forEach(logBook => {
							const totQuantity = logBook.log_book_details?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
							const totMinutes = logBook.log_book_details?.reduce((acc, item) => acc + item.minutes, 0) ?? 0;
							logBook.tot_quantity = totQuantity;
							logBook.tot_minutes = totMinutes;
						});
					}
					return response;
				})
			);
	}

	confirmRegistrations(data: { status: string; log_book_detail_ids: Array<string> }): Observable<CommonResponse<boolean>> {
		return this._http.patch<CommonResponse<boolean>>(`${environment.apiSchoolUrl}/career/director/log_book_details`, data);
	}

	saveEvaluation(data: FormData, school_id?: string, career_id?: string): Observable<CommonResponse<Evaluation>> {
		return this._http.post<CommonResponse<Evaluation>>(
			`${environment.apiSchoolUrl}/career/evaluations`,
			data,
			this.buildHeaders(school_id, career_id)
		);
	}

	editEvaluation(id: string, data: FormData, school_id?: string, career_id?: string): Observable<CommonResponse<Evaluation>> {
		data.append('_method', 'PUT');
		return this._http.post<CommonResponse<Evaluation>>(
			`${environment.apiSchoolUrl}/career/evaluations/${id}`,
			data,
			this.buildHeaders(school_id, career_id)
		);
	}

	deleteEvaluation(id: string): Observable<CommonResponse<boolean>> {
		return this._http.delete<CommonResponse<boolean>>(`${environment.apiSchoolUrl}/career/evaluations/${id}`);
	}

	updateExam(id: string, data: FormData, school_id?: string, career_id?: string): Observable<CommonResponse<CareerYear>> {
		data.append('_method', 'PATCH');
		return this._http.post<CommonResponse<CareerYear>>(
			`${environment.apiSchoolUrl}/career/career_years/${id}/exam`,
			data,
			this.buildHeaders(school_id, career_id)
		);
	}

	downloadExamFile(id: string, school_id?: string, career_id?: string): Observable<CommonResponse<any>> {
		return this._http.get<CommonResponse<any>>(
			`${environment.apiSchoolUrl}/career/career_years/${id}/download_exam_file`,
			this.buildHeaders(school_id, career_id)
		);
	}

	deleteExamFile(id: string, school_id?: string, career_id?: string): Observable<CommonResponse<boolean>> {
		return this._http.delete<CommonResponse<boolean>>(
			`${environment.apiSchoolUrl}/career/career_years/${id}/destroy_exam_file`,
			this.buildHeaders(school_id, career_id)
		);
	}

	downloadFile(id: string, school_id?: string, career_id?: string): Observable<CommonResponse<File>> {
		return this._http.get<CommonResponse<File>>(
			`${environment.apiSchoolUrl}/career/evaluations/${id}/download_file`,
			this.buildHeaders(school_id, career_id)
		);
	}

	deleteFile(id: string, school_id?: string, career_id?: string): Observable<CommonResponse<File>> {
		return this._http.delete<CommonResponse<File>>(
			`${environment.apiSchoolUrl}/career/evaluations/${id}/destroy_file`,
			this.buildHeaders(school_id, career_id)
		);
	}

	getSkillTotalizersForLogBookDetails(
		log_book_detail_ids: Array<string>
	): Observable<CommonResponse<{ totalizers: Array<any>; career_id: string }>> {
		return this._http.post<CommonResponse<{ totalizers: Array<any>; career_id: string }>>(
			`${environment.apiSchoolUrl}/career/log_book_details/totalizers`,
			{
				log_book_detail_ids,
			}
		);
	}

	getSkillTotalizersByYear(
		year: number,
		school_id?: string,
		career_id?: string
	): Observable<CommonResponse<{ totalizers: Array<any>; career_id: string }>> {
		return this._http.get<CommonResponse<{ totalizers: Array<any>; career_id: string }>>(
			`${environment.apiSchoolUrl}/career/log_book_details/skill_totalizers/year/${year}`,
			this.buildHeaders(school_id, career_id)
		);
	}

	assignSkillTotalizersByYear(
		year: number,
		data: { totalizer_ids: Array<string> },
		school_id?: string,
		career_id?: string
	): Observable<CommonResponse<boolean>> {
		return this._http.post<CommonResponse<boolean>>(
			`${environment.apiSchoolUrl}/career/log_book_details/skill_totalizers/year/${year}/assign`,
			data,
			this.buildHeaders(school_id, career_id)
		);
	}

	getSkillTotalizersByShift(
		shift_id: string,
		school_id?: string,
		career_id?: string
	): Observable<CommonResponse<{ totalizers: Array<any>; career_id: string }>> {
		return this._http.get<CommonResponse<{ totalizers: Array<any>; career_id: string }>>(
			`${environment.apiSchoolUrl}/career/shifts/${shift_id}/skill_totalizers`,
			this.buildHeaders(school_id, career_id)
		);
	}

	assignSkillTotalizersByShift(
		shift_id: string,
		data: { totalizer_ids: Array<string> },
		school_id?: string,
		career_id?: string
	): Observable<CommonResponse<boolean>> {
		return this._http.post<CommonResponse<boolean>>(
			`${environment.apiSchoolUrl}/career/shifts/${shift_id}/skill_totalizers/assign`,
			data,
			this.buildHeaders(school_id, career_id)
		);
	}
}
